import * as React from "react";

import {
  LOGIN_FORM_WINDOW_EVENTS,
  useJoinDrawer,
  useJoinDrawerUserExperience,
} from "../../Common";

const NOTIFY_ME_LOGIN_EVENT =
  "handle-pdp-notify-me-link-click-not-logged-in-user";
const NOTIFY_ME_ALREADY_LOGGED_IN_EVENT =
  "handle-pdp-notify-me-link-click-already-logged-in-user";
const NOTIFY_ME_REQUEST_ENDED_EVENT = "pdp-notify-me-request-ended";
const PENDING_NOTIFY_ME_REDIRECT_KEY = "bbnt-pdp-pending-notify-me";
type NotifyMeAlertType = "stock" | "price";

interface PdpNotifyMePresentationItem {
  title?: string;
  stockStatus?: string;
  price?: string;
  imageUrl?: string;
  alertType?: NotifyMeAlertType;
}

interface PendingNotifyMeRedirect {
  href: string;
  item?: PdpNotifyMePresentationItem;
}

interface NotifyMeAjaxResponse {
  success?: boolean;
  message?: string;
}

interface PdpNotifyMeUserExperienceConsumerInterface {
  presentingUserExperienceStep: NotifyMeUserExperience_Step;
  presentationItem: PdpNotifyMePresentationItem | null;
  notifyMeRequestErrorMessage: string | null;
  handleCloseOfTheRightDrawerAtAnyTime: () => void;
}

const defaultValue =
  {} as unknown as PdpNotifyMeUserExperienceConsumerInterface;
const PdpNotifyMeUserExperienceContext = React.createContext(defaultValue);

export enum NotifyMeUserExperience_Step {
  NOT_PRESENTING = "NOT_PRESENTING",
  CLOSING = "CLOSING",
  NEED_TO_LOGIN = "NEED_TO_LOGIN",
  ADDED_TO_NOTIFY_ME_SUCCESS_SCREEN = "ADDED_TO_NOTIFY_ME_SUCCESS_SCREEN",
}

export const PdpLoginDrawerTriggersProvider: React.FunctionComponent = (
  props
) => {
  const { handleClose: joinDrawer_handleClose } = useJoinDrawer();
  const { handleBeginTheJoinDrawerUserExperience } =
    useJoinDrawerUserExperience();

  const [presentingUserExperienceStep, setPresentingUserExperienceStep] =
    React.useState<NotifyMeUserExperience_Step>(
      NotifyMeUserExperience_Step.NOT_PRESENTING
    );
  const [presentationItem, setPresentationItem] =
    React.useState<PdpNotifyMePresentationItem | null>(null);
  const [notifyMeRequestErrorMessage, setNotifyMeRequestErrorMessage] =
    React.useState<string | null>(null);
  const [isSubmittingNotifyMe, setIsSubmittingNotifyMe] = React.useState(false);

  const resolveNotifyMeAlertTypeFromHref = React.useCallback(
    (href?: string): NotifyMeAlertType | undefined => {
      if (!href) {
        return undefined;
      }
      if (href.includes("/productalert/add/price/")) {
        return "price";
      }
      if (href.includes("/productalert/add/stock/")) {
        return "stock";
      }
      return undefined;
    },
    []
  );

  const hydratePresentationItem = React.useCallback(
    (
      item: PdpNotifyMePresentationItem | undefined,
      href: string
    ): PdpNotifyMePresentationItem | null => {
      if (!item) {
        return null;
      }
      return {
        ...item,
        alertType: item.alertType || resolveNotifyMeAlertTypeFromHref(href),
      };
    },
    [resolveNotifyMeAlertTypeFromHref]
  );

  const dispatchNotifyMeRequestEnded = React.useCallback(() => {
    window.dispatchEvent(new Event(NOTIFY_ME_REQUEST_ENDED_EVENT));
  }, []);

  const readPendingNotifyMeRedirect = React.useCallback(() => {
    const inMemoryPendingRedirect = (window as any)
      .__bbntPdpPendingNotifyMeRedirect as PendingNotifyMeRedirect | undefined;
    if (inMemoryPendingRedirect?.href) {
      return inMemoryPendingRedirect;
    }

    const sessionStoragePayload = window.sessionStorage.getItem(
      PENDING_NOTIFY_ME_REDIRECT_KEY
    );
    if (!sessionStoragePayload) {
      return null;
    }

    try {
      const parsedPayload = JSON.parse(
        sessionStoragePayload
      ) as PendingNotifyMeRedirect;
      if (parsedPayload?.href) {
        return parsedPayload;
      }
    } catch (error) {
      console.error(error);
    }

    return null;
  }, []);

  const clearPendingNotifyMeRedirect = React.useCallback(() => {
    delete (window as any).__bbntPdpPendingNotifyMeRedirect;
    window.sessionStorage.removeItem(PENDING_NOTIFY_ME_REDIRECT_KEY);
  }, []);

  const handleCloseOfTheRightDrawerAtAnyTime =
    React.useCallback(() => {
      if (
        presentingUserExperienceStep ===
        NotifyMeUserExperience_Step.ADDED_TO_NOTIFY_ME_SUCCESS_SCREEN
      ) {
        setPresentingUserExperienceStep(NotifyMeUserExperience_Step.CLOSING);
      } else {
        clearPendingNotifyMeRedirect();
        setPresentationItem(null);
        setNotifyMeRequestErrorMessage(null);
        setPresentingUserExperienceStep(
          NotifyMeUserExperience_Step.NOT_PRESENTING
        );
        dispatchNotifyMeRequestEnded();
      }
    }, [
      presentingUserExperienceStep,
      clearPendingNotifyMeRedirect,
      dispatchNotifyMeRequestEnded,
    ]);

  const handleNotifyMeLoginTrigger = React.useCallback(
    (event: Event) => {
      const customEvent = event as CustomEvent<{
        href?: string;
        item?: PdpNotifyMePresentationItem;
      }>;
      const href = customEvent?.detail?.href;
      const item = customEvent?.detail?.item;

      if (isSubmittingNotifyMe) {
        return;
      }

      if (!href) {
        return;
      }

      const payload: PendingNotifyMeRedirect = {
        href,
        item: hydratePresentationItem(item, href) || undefined,
      };
      (window as any).__bbntPdpPendingNotifyMeRedirect = payload;
      window.sessionStorage.setItem(
        PENDING_NOTIFY_ME_REDIRECT_KEY,
        JSON.stringify(payload)
      );

      setPresentationItem(hydratePresentationItem(item, href));
      setNotifyMeRequestErrorMessage(null);
      setPresentingUserExperienceStep(NotifyMeUserExperience_Step.NEED_TO_LOGIN);
      handleBeginTheJoinDrawerUserExperience();
    },
    [
      handleBeginTheJoinDrawerUserExperience,
      hydratePresentationItem,
      isSubmittingNotifyMe,
    ]
  );

  const showNotifyMeSuccessScreen = React.useCallback(
    async (pendingRedirect: PendingNotifyMeRedirect) => {
      const buildNotifyMeAjaxUrl = (href: string): string => {
        const url = new URL(href, window.location.origin);
        url.searchParams.set("ajax", "1");
        return url.toString();
      };

      setIsSubmittingNotifyMe(true);

      try {
        const response = await fetch(buildNotifyMeAjaxUrl(pendingRedirect.href), {
          method: "GET",
          credentials: "same-origin",
          headers: {
            Accept: "application/json",
            "X-Requested-With": "XMLHttpRequest",
          },
          redirect: "follow",
        });

        const contentType = response.headers.get("content-type") || "";
        if (!contentType.includes("application/json")) {
          throw new Error("Notify me request did not return JSON response.");
        }

        const data = (await response.json()) as NotifyMeAjaxResponse;
        if (!response.ok || !data?.success) {
          const message =
            data?.message || `Notify me request failed with status ${response.status}`;
          throw new Error(message);
        }

        clearPendingNotifyMeRedirect();
        setPresentationItem(
          hydratePresentationItem(pendingRedirect.item, pendingRedirect.href)
        );
        setNotifyMeRequestErrorMessage(null);
        setPresentingUserExperienceStep(
          NotifyMeUserExperience_Step.ADDED_TO_NOTIFY_ME_SUCCESS_SCREEN
        );
      } finally {
        setIsSubmittingNotifyMe(false);
        dispatchNotifyMeRequestEnded();
      }
    },
    [
      clearPendingNotifyMeRedirect,
      dispatchNotifyMeRequestEnded,
      hydratePresentationItem,
    ]
  );

  const handleNotifyMeAlreadyLoggedInTrigger = React.useCallback(
    async (event: Event) => {
      const customEvent = event as CustomEvent<{
        href?: string;
        item?: PdpNotifyMePresentationItem;
      }>;
      const href = customEvent?.detail?.href;
      const item = customEvent?.detail?.item;

      if (isSubmittingNotifyMe) {
        return;
      }

      if (!href) {
        return;
      }

      const hydratedPresentationItem = hydratePresentationItem(item, href);

      try {
        await showNotifyMeSuccessScreen({
          href,
          item: hydratedPresentationItem || undefined,
        });
      } catch (error) {
        console.error(error);
        clearPendingNotifyMeRedirect();
        setPresentationItem(hydratedPresentationItem);
        setNotifyMeRequestErrorMessage(
          error instanceof Error && error.message
            ? error.message
            : "The alert subscription couldn't update at this time. Please try again later."
        );
        setPresentingUserExperienceStep(
          NotifyMeUserExperience_Step.ADDED_TO_NOTIFY_ME_SUCCESS_SCREEN
        );
        dispatchNotifyMeRequestEnded();
      }
    },
    [
      showNotifyMeSuccessScreen,
      clearPendingNotifyMeRedirect,
      dispatchNotifyMeRequestEnded,
      hydratePresentationItem,
      isSubmittingNotifyMe,
    ]
  );

  const handleLoginSuccess = React.useCallback(async () => {
    const pendingRedirect = readPendingNotifyMeRedirect();
    if (!pendingRedirect?.href) {
      return;
    }

    if (
      presentingUserExperienceStep !== NotifyMeUserExperience_Step.NEED_TO_LOGIN
    ) {
      return;
    }

    try {
      joinDrawer_handleClose();
      await showNotifyMeSuccessScreen(pendingRedirect);
    } catch (error) {
      console.error(error);
      clearPendingNotifyMeRedirect();
      setPresentationItem(pendingRedirect.item || null);
      setNotifyMeRequestErrorMessage(
        error instanceof Error && error.message
          ? error.message
          : "The alert subscription couldn't update at this time. Please try again later."
      );
      setPresentingUserExperienceStep(
        NotifyMeUserExperience_Step.ADDED_TO_NOTIFY_ME_SUCCESS_SCREEN
      );
    }
  }, [
    readPendingNotifyMeRedirect,
    presentingUserExperienceStep,
    joinDrawer_handleClose,
    clearPendingNotifyMeRedirect,
    showNotifyMeSuccessScreen,
  ]);

  React.useEffect(() => {
    window.addEventListener(NOTIFY_ME_LOGIN_EVENT, handleNotifyMeLoginTrigger);
    window.addEventListener(
      NOTIFY_ME_ALREADY_LOGGED_IN_EVENT,
      handleNotifyMeAlreadyLoggedInTrigger
    );
    window.addEventListener(
      LOGIN_FORM_WINDOW_EVENTS.LOGIN_SUCCESS,
      handleLoginSuccess
    );
    return () => {
      window.removeEventListener(
        NOTIFY_ME_LOGIN_EVENT,
        handleNotifyMeLoginTrigger
      );
      window.removeEventListener(
        NOTIFY_ME_ALREADY_LOGGED_IN_EVENT,
        handleNotifyMeAlreadyLoggedInTrigger
      );
      window.removeEventListener(
        LOGIN_FORM_WINDOW_EVENTS.LOGIN_SUCCESS,
        handleLoginSuccess
      );
    };
  }, [
    handleNotifyMeLoginTrigger,
    handleNotifyMeAlreadyLoggedInTrigger,
    handleLoginSuccess,
  ]);

  const value = React.useMemo(
    () => ({
      presentingUserExperienceStep,
      presentationItem,
      notifyMeRequestErrorMessage,
      handleCloseOfTheRightDrawerAtAnyTime,
    }),
    [
      presentingUserExperienceStep,
      presentationItem,
      notifyMeRequestErrorMessage,
      handleCloseOfTheRightDrawerAtAnyTime,
    ]
  );

  return (
    <PdpNotifyMeUserExperienceContext.Provider value={value}>
      {props.children || []}
    </PdpNotifyMeUserExperienceContext.Provider>
  );
};

export const usePdpNotifyMeUserExperience = () => {
  const context = React.useContext(PdpNotifyMeUserExperienceContext);
  if (context === defaultValue) {
    throw new Error(
      "usePdpNotifyMeUserExperience must be used within a PdpLoginDrawerTriggersProvider"
    );
  }
  return context;
};
